/**
 * HTTPS Enforcer Utility
 *
 * This utility ensures that the application always runs over HTTPS
 * by checking the current protocol and redirecting if necessary.
 *
 * This provides a client-side fallback in case server-side redirects fail.
 */

export class HttpsEnforcer {
  /**
   * Enforces HTTPS by redirecting to HTTPS if currently on HTTP
   * @param forceRedirect - If true, forces redirect even in development
   */
  static enforceHttps(forceRedirect = false): void {
    // Skip in development unless forced
    if (!forceRedirect && this.isDevelopment()) {
      return;
    }

    // Check if we're on HTTP
    if (this.isHttp()) {
      this.redirectToHttps();
    }
  }

  /**
   * Checks if the current protocol is HTTP
   */
  private static isHttp(): boolean {
    return window.location.protocol === 'http:';
  }

  /**
   * Checks if we're in development environment
   */
  private static isDevelopment(): boolean {
    return (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('localhost') ||
      window.location.port !== ''
    );
  }

  /**
   * Redirects to HTTPS version of the current URL
   */
  private static redirectToHttps(): void {
    const httpsUrl = window.location.href.replace('http:', 'https:');

    // Use replace instead of assign to avoid back button issues
    window.location.replace(httpsUrl);
  }

  /**
   * Sets up automatic HTTPS enforcement on page load
   * This should be called as early as possible in the application lifecycle
   */
  static setupAutoEnforcement(): void {
    // Enforce HTTPS immediately
    this.enforceHttps();

    // Also enforce on any navigation changes (for SPAs)
    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', () => {
        this.enforceHttps();
      });

      // Monitor for any programmatic location changes
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;

      history.pushState = function (...args) {
        originalPushState.apply(history, args);
        HttpsEnforcer.enforceHttps();
      };

      history.replaceState = function (...args) {
        originalReplaceState.apply(history, args);
        HttpsEnforcer.enforceHttps();
      };
    }
  }

  /**
   * Checks if the current connection is secure
   */
  static isSecureConnection(): boolean {
    return (
      window.location.protocol === 'https:' || this.isDevelopment() // Consider localhost as secure for development
    );
  }

  /**
   * Gets the HTTPS version of a given URL
   */
  static getHttpsUrl(url: string): string {
    if (url.startsWith('http:')) {
      return url.replace('http:', 'https:');
    }
    return url;
  }
}
